import { combinedDateTime } from '@/lib/utils';
import { MediaResponseSchema } from '@/types/media.types';
import { ATTEMPT_TYPE, MAX_IMAGE_LENGTH_ATTEMPT } from '@/types/workOrderAttempt.types';
import { z } from 'zod';

export const WorkOrderAttemptMediaSchema = z.object({
  attemptMediaId: z.string().optional(),
  mediaId: z.string().optional(),
  createdAt: z.string().optional(),
  attemptId: z.string().optional(),
  media: MediaResponseSchema,
});

export const AttemptSchema = z
  .object({
    type: z.nativeEnum(ATTEMPT_TYPE).optional().nullish(),
    attemptId: z.string().optional().nullish(),
    serverName: z.string().optional().nullish(),
    workOrderId: z.string().optional().nullish(),
    dateOfAttempt: z.date().nullish(),
    timeOfAttempt: z.date().nullish(),
    description: z.string().nullish(),
    personAccepting: z.string().nullish(),
    relation: z.string().nullish(),
    age: z.string().nullish(),
    height: z.string().nullish(),
    weight: z.string().nullish(),
    hair: z.string().nullish(),
    sex: z.string().nullish(),
    race: z.string().nullish(),
    otherDetails: z.string().nullish(),
    deletedAt: z.string().nullish(),
    images: z
      .array(
        z.object({
          id: z.string().nullish(),
          name: z.string().nullish(),
          image: z.any().nullish(),
          mediaId: z.string().nullish(),
          mediaData: z.any().nullish(),
        })
      )
      .nullish(),
    workOrderMedia: z.array(WorkOrderAttemptMediaSchema).nullish(),
    emailAddress: z.string().trim().email('Email address is invalid').nullish(),
    emailSentDate: z.date().nullish(),
    emailAcknowledgementDate: z.date().nullish(),
    remarks: z.string().nullish(),
    trackingNumber: z.string().nullish(),
    additionalInformation: z.string().nullish(),
  })
  .superRefine((data, ctx) => {
    const anyFieldFilled = Object.entries(data).some(([, value]) => {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim() !== '';
      return true;
    });

    if (anyFieldFilled) {
      if (!data.type) {
        ctx.addIssue({
          path: ['type'],
          code: 'custom',
          message: 'Type is required',
        });
      }
      if (data.age) {
        const ageNum = Number(data.age);
        if (ageNum < 0 || ageNum > 130) {
          ctx.addIssue({
            path: ['age'],
            code: 'custom',
            message: 'Age is invalid',
          });
        }
      }
      if (!data.dateOfAttempt) {
        ctx.addIssue({
          path: ['dateOfAttempt'],
          code: 'custom',
          message: 'Date of attempt is required',
        });
      }
      if (!data.timeOfAttempt) {
        ctx.addIssue({
          path: ['timeOfAttempt'],
          code: 'custom',
          message: 'Time of attempt is required',
        });
      }

      if (data.type === ATTEMPT_TYPE.PERSONAL || data.type === ATTEMPT_TYPE.SUB) {
        if (!data.personAccepting)
          ctx.addIssue({
            path: ['personAccepting'],
            code: 'custom',
            message: 'Person accepting is required',
          });
        if (!data.relation)
          ctx.addIssue({
            path: ['relation'],
            code: 'custom',
            message: 'Title/ Relation to Serve is required',
          });
        if (!data.age)
          ctx.addIssue({
            path: ['age'],
            code: 'custom',
            message: 'Age is required',
          });
        if (!data.height)
          ctx.addIssue({
            path: ['height'],
            code: 'custom',
            message: 'Height is required',
          });
        if (!data.weight)
          ctx.addIssue({
            path: ['weight'],
            code: 'custom',
            message: 'Weight is required',
          });
        if (!data.hair)
          ctx.addIssue({
            path: ['hair'],
            code: 'custom',
            message: 'Hair color is required',
          });
        if (!data.sex)
          ctx.addIssue({
            path: ['sex'],
            code: 'custom',
            message: 'Sex is required',
          });
        if (!data.race)
          ctx.addIssue({
            path: ['race'],
            code: 'custom',
            message: 'Race is required',
          });
      }

      if (data.type === ATTEMPT_TYPE.BY_EMAIL) {
        if (!data.emailAddress)
          ctx.addIssue({
            path: ['emailAddress'],
            code: 'custom',
            message: 'Email address is required',
          });
      }

      if (
        data.dateOfAttempt &&
        data.timeOfAttempt &&
        combinedDateTime(data.dateOfAttempt ?? undefined, data.timeOfAttempt ?? undefined) > new Date()
      ) {
        ctx.addIssue({
          path: ['timeOfAttempt'],
          code: 'custom',
          message: 'Time of attempt cannot be in the future',
        });
      }
      if (data.images) {
        if (data.images.length > MAX_IMAGE_LENGTH_ATTEMPT)
          ctx.addIssue({
            path: ['images'],
            code: 'custom',
            message: `A maximum of ${MAX_IMAGE_LENGTH_ATTEMPT} images is allowed`,
          });
      }
    }
  });

export const WorkOrderAttemptsSchema = z.object({
  number: z.string().nullish(),
  workOrderId: z.string().nullish(),
  server: z
    .object({
      userId: z.string(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullish(),
  attempts: z.array(AttemptSchema).nullish(),
});
